import { mercuryApi } from '$api'
import { DaysOfWeek, SelectedShifts } from '$constants'
import {
  Nullable,
  Roster,
  RosterTemplate,
  transformRosterOnGet,
  transformRosterTemplateOnGet,
  transformRosterTemplateOnSave,
} from '$models'
import { createQueryString } from '$utils'
import { Dayjs } from 'dayjs'

//This type is created here because it is only used for the publish roster method
//If we place it closer to the PublishRostr.Page.tsx then the service will need to import it from there
//and we don't want that because the services shouldn't depend on files that are within the
//routes filder structure
export type StaffMemberRosteredShiftsInfo = {
  staffMemberId: string
  email?: string
  roleIds: string[]
  name?: string
}

/**
 * Publish rosters
 * @param rosterId to publish
 * @param staffMemberRosteredShiftsInfo - is a KeyValue pair where the key is the
 * staffMemberId and the value is the list of roleIds related to that staffMember
 * @param publishRosterToWeb
 * @param lockRoster
 * @param additionalNote
 * @returns Promise<Roster>
 */
export const publish = async (
  rosterId: string,
  staffMemberRosteredShiftsInfo: StaffMemberRosteredShiftsInfo[],
  publishRosterToWeb: boolean,
  lockRoster: boolean,
  additionalNote: string | null,
  selectedShifts: SelectedShifts | undefined
): Promise<Roster> => {
  const publishedRoster = await mercuryApi.update(
    `/time/rosters/${rosterId}/publish`,
    {
      staffMemberRosteredShiftsInfo,
      publishRosterToWeb,
      lockRoster,
      additionalNote,
      selectedShifts,
    }
  )

  return transformRosterOnGet(publishedRoster)
}

/**
 * Get Roster by Id
 * @param rosterId - roster id to get
 * @returns Promise<Roster>
 */
export const getById = async (
  rosterId: string,
  includeDeletedRosteredShifts?: boolean
): Promise<Roster> => {
  const query = createQueryString({
    includeDeletedRosteredShifts,
  })
  const result = await mercuryApi.get<Roster>(
    `/time/rosters/${rosterId}?${query}`
  )
  const updatedRoster = transformRosterOnGet(result)
  return updatedRoster
}

/**
 * Get Rosters by date range
 * @param startTime
 * @param endTime
 * @returns Promise<Roster>
 */
export const getRostersByDateRange = async (
  startTime: Dayjs,
  endTime: Dayjs
): Promise<Roster[]> => {
  const query = createQueryString({
    startTime: startTime.utc().format(),
    endTime: endTime.utc().format(),
  })

  const result = await mercuryApi.get<Roster[]>(`/time/rosters?${query}`)
  const updatedRosters = result.map(transformRosterOnGet)
  return updatedRosters
}

/**
 * Get Roster Templates
 * @param includeDeleted
 * @returns Promise<RosterTemplate[]>
 */
export const getTemplates = async (
  includeDeleted: boolean
): Promise<RosterTemplate[]> => {
  const query = createQueryString({
    includeDeleted,
  })

  const result = await mercuryApi.get<RosterTemplate[]>(
    `/time/rosters/templates?${query}`
  )

  const updatedRosterTemplates = result.map(transformRosterTemplateOnGet)
  return updatedRosterTemplates
}

/**
 * Create roster template
 * @returns Promise<RosterTemplate>
 */
export const createRosterTemplate = async (): Promise<RosterTemplate> => {
  const newRosterTemplate = await mercuryApi.add(`/time/rosters/templates`, {})

  const transformedRosterTemplateWhenGet =
    transformRosterTemplateOnGet(newRosterTemplate)

  return transformedRosterTemplateWhenGet
}

/**
 * Update roster template
 * @returns Promise<RosterTemplate>
 */
export const updateRosterTemplate = async (
  rosterTemplate: RosterTemplate
): Promise<RosterTemplate> => {
  const transformedRosterTemplate =
    transformRosterTemplateOnSave(rosterTemplate)

  const updatedRosterTemplate = await mercuryApi.update(
    `/time/rosters/${rosterTemplate.id}`,
    transformedRosterTemplate
  )

  const transformedRosterTemplateWhenGet = transformRosterTemplateOnGet(
    updatedRosterTemplate
  )

  return transformedRosterTemplateWhenGet
}

/**
 * Create roster template
 * @returns Promise<RosterTemplate>
 */
export const copyRosterTemplate = async (
  rosterId: string,
  templateName: string
): Promise<RosterTemplate> => {
  const copiedRosterTemplate = await mercuryApi.add(
    `/time/rosters/templates/copy`,
    {
      id: rosterId,
      templateName,
    }
  )

  const transformedRosterTemplateWhenGet =
    transformRosterTemplateOnGet(copiedRosterTemplate)

  return transformedRosterTemplateWhenGet
}

/**
 *
 * @param rosterTemplateId - roster template id (if the copy is done from a roster template)
 * @param sourceWeekStart - srouce week start date (if the copy is done from a week range)
 * @param destinationWeekStart - destination week start date
 * @param days - array of days of the week that needs to be copied to the new roster
 * @param staffMemberRosteredShiftsInfo this variable contains what staff mnembers and roles needs to be copied to the new roster
 * @returns Promise<void>
 */
export const copyRoster = async (
  rosterTemplateId: Nullable<string>,
  sourceWeekStart: Nullable<Dayjs>,
  destinationWeekStart: Dayjs,
  days: DaysOfWeek[],
  staffMemberRosteredShiftsInfo: StaffMemberRosteredShiftsInfo[]
): Promise<void> => {
  //It does return NoContent()
  return await mercuryApi.add(`/time/rosters/copy-new`, {
    rosterTemplateId,
    sourceWeekStart,
    destinationWeekStart,
    days,
    staffMemberRosteredShiftsInfo,
  })
}

/**
 * Sets the public roster token setting through the rosters controller
 * @param token string uuid token
 * @returns void
 */
export const updatePublicToken = async (token: string): Promise<void> => {
  return await mercuryApi.add(`/time/rosters/token?token=${token}`, {})
}
